import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { gql } from '../../graphql/client';

/**
 * Per-app Theme editor. Edits the Theme row currently linked from
 * `FrontendApp.theme_id` (passed in via `theme`); if the app has no
 * theme yet, the section offers a "Create theme" CTA that inserts a
 * fresh row with the form values and links it to the app.
 *
 * This deliberately replaces the previous global-multi-theme editor
 * that lived in the View Builder right rail. App Settings is the
 * natural home for theme — themes are app-level, not view-level —
 * and the per-app shape avoids the "which theme is even active for
 * this app?" confusion the picker introduced.
 */

const M_UPDATE_THEME = /* GraphQL */ `
  mutation UpdateThemeBySection(
    $id: uuid!
    $name: String!
    $primary: String, $accent: String, $text: String, $bg: String, $font: String
  ) {
    update_Theme_by_pk(
      pk_columns: { id: $id }
      _set: {
        name: $name, primary_color: $primary, accent_color: $accent,
        text_color: $text, background_color: $bg, font_family: $font
      }
    ) { id name primary_color accent_color text_color background_color font_family }
  }
`;

const M_INSERT_AND_LINK_THEME = /* GraphQL */ `
  mutation InsertAndLinkTheme(
    $appId: uuid!
    $name: String!
    $primary: String, $accent: String, $text: String, $bg: String, $font: String
  ) {
    inserted: insert_Theme_one(object: {
      name: $name, primary_color: $primary, accent_color: $accent,
      text_color: $text, background_color: $bg, font_family: $font
    }) { id }
  }
`;

const M_LINK_APP_THEME = /* GraphQL */ `
  mutation LinkAppTheme($appId: uuid!, $themeId: uuid!) {
    update_FrontendApp_by_pk(
      pk_columns: { id: $appId }
      _set: { theme_id: $themeId }
    ) { id theme_id }
  }
`;

export interface ThemeShape {
  id: string;
  name: string;
  primary_color: string | null;
  accent_color: string | null;
  text_color: string | null;
  background_color: string | null;
  font_family: string | null;
}

export interface ThemeSectionProps {
  appId: string;
  /** Currently-linked theme; null if the app hasn't been themed yet. */
  theme: ThemeShape | null;
}

const DEFAULT_THEME: Omit<ThemeShape, 'id'> = {
  name: 'App Theme',
  primary_color: '#4F46E5',
  accent_color: '#F59E0B',
  text_color: '#0F172A',
  background_color: '#F8FAFC',
  font_family: 'system-ui',
};

export function ThemeSection({ appId, theme }: ThemeSectionProps) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState<Omit<ThemeShape, 'id'>>(() => ({
    name: theme?.name ?? DEFAULT_THEME.name,
    primary_color: theme?.primary_color ?? DEFAULT_THEME.primary_color,
    accent_color: theme?.accent_color ?? DEFAULT_THEME.accent_color,
    text_color: theme?.text_color ?? DEFAULT_THEME.text_color,
    background_color: theme?.background_color ?? DEFAULT_THEME.background_color,
    font_family: theme?.font_family ?? DEFAULT_THEME.font_family,
  }));
  const [dirty, setDirty] = useState(false);

  // Resync the form when the underlying theme changes (e.g. after another
  // tab edits it, or after first creation).
  useEffect(() => {
    if (!theme) return;
    setForm({
      name: theme.name,
      primary_color: theme.primary_color,
      accent_color: theme.accent_color,
      text_color: theme.text_color,
      background_color: theme.background_color,
      font_family: theme.font_family,
    });
    setDirty(false);
  }, [theme?.id, theme?.name, theme?.primary_color, theme?.accent_color,
      theme?.text_color, theme?.background_color, theme?.font_family]);

  const set = <K extends keyof typeof form>(k: K, v: string) => {
    setForm((p) => ({ ...p, [k]: v }));
    setDirty(true);
  };

  const save = useMutation({
    mutationFn: async () => {
      const vars = {
        name: form.name,
        primary: form.primary_color || null,
        accent: form.accent_color || null,
        text: form.text_color || null,
        bg: form.background_color || null,
        font: form.font_family || null,
      };
      if (theme?.id) {
        return gql(M_UPDATE_THEME, { id: theme.id, ...vars });
      }
      // No linked theme yet — insert and link to the app in two
      // sequential calls. The shell + builder pick up the new theme on
      // the next FrontendApp refetch.
      const inserted = await gql<{ inserted: { id: string } }>(
        M_INSERT_AND_LINK_THEME,
        { appId, ...vars },
      );
      await gql(M_LINK_APP_THEME, { appId, themeId: inserted.inserted.id });
    },
    onSuccess() {
      setDirty(false);
      queryClient.invalidateQueries({ queryKey: ['frontend-app', appId] });
    },
  });

  return (
    <div>
      <div style={s.preview} aria-label="Theme preview">
        <div style={{ ...s.swatch, backgroundColor: form.primary_color || '#fff' }} title="Primary" />
        <div style={{ ...s.swatch, backgroundColor: form.accent_color || '#fff' }} title="Accent" />
        <div style={{ ...s.swatch, backgroundColor: form.text_color || '#fff', borderColor: '#94A3B8' }} title="Text" />
        <div style={{ ...s.swatch, backgroundColor: form.background_color || '#fff', borderColor: '#94A3B8' }} title="Background" />
        <span style={{ ...s.previewSample, color: form.text_color ?? '#0F172A', fontFamily: form.font_family ?? 'system-ui' }}>
          The quick brown fox · {form.font_family || 'system-ui'}
        </span>
      </div>

      <Field label="Theme name">
        <input value={form.name} onChange={(e) => set('name', e.target.value)} style={s.input} />
      </Field>

      <div style={s.colorGrid}>
        <ColorField label="Primary" value={form.primary_color ?? ''} onChange={(v) => set('primary_color', v)} />
        <ColorField label="Accent" value={form.accent_color ?? ''} onChange={(v) => set('accent_color', v)} />
        <ColorField label="Text" value={form.text_color ?? ''} onChange={(v) => set('text_color', v)} />
        <ColorField label="Background" value={form.background_color ?? ''} onChange={(v) => set('background_color', v)} />
      </div>

      <Field label="Font family" hint='e.g. "system-ui", "Inter", "Roboto"'>
        <input value={form.font_family ?? ''} onChange={(e) => set('font_family', e.target.value)} style={s.input} />
      </Field>

      <div style={s.actionsRow}>
        <button
          type="button"
          disabled={!dirty || save.isPending}
          onClick={() => save.mutate()}
          style={dirty ? s.saveBtn : s.saveBtnDisabled}
        >
          {save.isPending ? 'Saving…' : theme ? (dirty ? 'Save theme' : 'Saved') : 'Create theme'}
        </button>
        {save.isError && <span style={s.error}>{(save.error as Error).message}</span>}
      </div>
    </div>
  );
}

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <label style={s.field}>
      <span style={s.fieldLabel}>{label}</span>
      {children}
      {hint && <span style={s.fieldHint}>{hint}</span>}
    </label>
  );
}

function ColorField({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <label style={s.colorField}>
      <span style={s.fieldLabel}>{label}</span>
      <div style={s.colorRow}>
        <input
          type="color"
          value={value || '#ffffff'}
          onChange={(e) => onChange(e.target.value)}
          style={s.colorChip}
        />
        <input value={value} onChange={(e) => onChange(e.target.value)} style={{ ...s.input, flex: 1 }} placeholder="#000000" />
      </div>
    </label>
  );
}

const s: Record<string, React.CSSProperties> = {
  preview: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    padding: '12px 14px',
    marginBottom: 16,
    backgroundColor: '#F8FAFC',
    border: '1px solid #E2E8F0',
    borderRadius: 8,
  },
  swatch: {
    width: 28,
    height: 28,
    borderRadius: 14,
    border: '1px solid #fff',
    flexShrink: 0,
    boxShadow: '0 0 0 1px rgba(15,23,42,0.06)',
  },
  previewSample: {
    fontSize: 12,
    color: '#0F172A',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  colorGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 },
  colorField: { display: 'flex', flexDirection: 'column', gap: 4 },
  colorRow: { display: 'flex', gap: 6 },
  colorChip: { width: 36, height: 30, padding: 0, border: '1px solid #CBD5E1', borderRadius: 4, cursor: 'pointer' },
  field: { display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 },
  fieldLabel: { fontSize: 12, fontWeight: 600, color: '#475569' },
  fieldHint: { fontSize: 11, color: '#94A3B8' },
  input: { padding: '8px 10px', fontSize: 13, border: '1px solid #CBD5E1', borderRadius: 4 },
  actionsRow: { display: 'flex', alignItems: 'center', gap: 12, marginTop: 4 },
  saveBtn: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#fff',
    backgroundColor: '#4F46E5', border: 'none', borderRadius: 6, cursor: 'pointer',
  },
  saveBtnDisabled: {
    padding: '8px 14px', fontSize: 13, fontWeight: 600, color: '#94A3B8',
    backgroundColor: '#E2E8F0', border: 'none', borderRadius: 6, cursor: 'default',
  },
  error: { fontSize: 12, color: '#c00' },
};
